/**
 * The Occupancy page's data (POS2-1259 CP3).
 *
 * One query against decision 5's feed — `GET /api/v1/occupancy/covers` returns every day in the
 * range already composed (base, its source, events, total), so the page does no derivation of its
 * own. That is the point of decision 5: the page, the homepage widget, the KPIs and the report
 * payload all read the same shape, and none of them works covers out a second time.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";
import { splitWeekByPattern, sumCoversForRange, weekDates } from "@/lib/occupancy";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// Six, matching the CHECK on `site_events.event_type`. `general` and `other` were added for the
// POS2-1350 import (Mark, 2026-09-18); the old platform's EXPO folds into `conference`, which the
// catalogue shows as "Conference & Expo" rather than giving it a value of its own.
export const EVENT_TYPES = [
  "wedding",
  "conference",
  "function",
  "banquet",
  "general",
  "other",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export type ServiceStyle = "plated" | "buffet" | "family_style" | "canapes" | "day_delegate";

export type OccupancyEvent = {
  id: string;
  site_id: string;
  date: string;
  name: string;
  event_type: EventType;
  covers: number;
  start_time: string | null;
  end_time: string | null;
  room: string | null;
  service_style: ServiceStyle | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type DayCovers = {
  date: string;
  /** Null when the day has no base at all — a date before the site's first pattern version.
   *  Not zero: decision 8 renders that as N/A rather than dividing waste by an invented figure. */
  base: number | null;
  base_source: "pattern" | "override" | "none";
  weekly_entered: boolean;
  events: OccupancyEvent[];
  /** Null only when the day has no base *and* no events. */
  total: number | null;
  /** Who last wrote this day's override. All null when the base came from the pattern or from
   *  nothing — there is no row, so there is nobody to name. */
  entered_by_id: string | null;
  entered_by_name: string | null;
  entered_at: string | null;
};

export function useOccupancyCovers(siteId: string | null, dateFrom: string, dateTo: string) {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return useQuery<DayCovers[]>({
    queryKey: ["occupancy", "covers", siteId, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams({
        site_id: siteId as string,
        date_from: dateFrom,
        date_to: dateTo,
      });
      const res = await fetch(`${API_URL}/api/v1/occupancy/covers?${params.toString()}`, {
        headers,
      });
      if (!res.ok) throw await apiError(t, res);
      return ((await res.json()) as { data: DayCovers[] }).data;
    },
    enabled: !!siteId && !!headers.Authorization.replace("Bearer ", ""),
  });
}

export type BasePatternDay = { weekday: number; covers: number };

export type BasePattern = {
  site_id: string;
  effective_from: string;
  days: BasePatternDay[];
};

export type BaseOverride = {
  id: string;
  site_id: string;
  date: string;
  covers: number;
  source: "page" | "widget" | "backfill";
};

export type OverrideList = {
  items: BaseOverride[];
  /** More entered days exist than were returned. The list says so rather than implying it is
   *  everything — PostgREST would otherwise truncate silently at its own cap. */
  has_more: boolean;
};

export type WasteBasis = {
  grams_per_cover: number;
  /** Drives the basis line: a measured figure reads differently from a borrowed one. */
  source: "site_average" | "target";
  cost_per_kg: number | null;
  currency: string;
};

export type EventWrite = {
  site_id: string;
  date: string;
  name: string;
  event_type: EventType;
  covers: number;
  start_time: string | null;
  end_time: string | null;
  room: string | null;
  service_style: ServiceStyle | null;
  notes: string | null;
};

/** Thrown when the API refuses a booking because the room is already taken (decision 6). */
export class RoomConflictError extends Error {
  constructor(public readonly clashing: OccupancyEvent[]) {
    super("room conflict");
    this.name = "RoomConflictError";
  }
}

function useOccupancyRequest() {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${API_URL}/api/v1/occupancy${path}`, { headers, ...init });
    if (res.status === 409) {
      // The conflict body is a decision the drawer has to present, not an error to toast.
      const body = (await res.json()) as { detail: { clashing: OccupancyEvent[] } };
      throw new RoomConflictError(body.detail.clashing);
    }
    if (!res.ok) throw await apiError(t, res);
    if (res.status === 204) return undefined as T;
    return ((await res.json()) as { data: T }).data;
  };
}

export function useWasteBasis(siteId: string | null) {
  const request = useOccupancyRequest();
  const headers = useApiHeaders();
  return useQuery<WasteBasis>({
    queryKey: ["occupancy", "waste-basis", siteId],
    queryFn: () => request<WasteBasis>(`/waste-basis?site_id=${siteId as string}`),
    enabled: !!siteId && !!headers.Authorization.replace("Bearer ", ""),
    // An hour, because this is the most expensive read in the feature and the cheapest one to
    // hold on to: `/waste-basis` aggregates a year of waste per civil day and then derives a
    // year of covers to intersect with it, and what comes back is a **12-month rolling
    // average**. It cannot move enough within a session to be worth re-running, and at five
    // minutes it was re-running on most visits to the page.
    staleTime: 60 * 60_000,
  });
}

export function useBasePattern(siteId: string | null, on: string) {
  const request = useOccupancyRequest();
  const headers = useApiHeaders();
  return useQuery<BasePattern | null>({
    queryKey: ["occupancy", "base-pattern", siteId, on],
    queryFn: () =>
      request<BasePattern | null>(`/base-pattern?site_id=${siteId as string}&on=${on}`),
    enabled: !!siteId && !!headers.Authorization.replace("Bearer ", ""),
  });
}

/** Every occupancy write invalidates the same root key — one derivation, one cache to refresh. */
function useOccupancyInvalidate() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ["occupancy"] });
}

export function useSaveEvent() {
  const request = useOccupancyRequest();
  const invalidate = useOccupancyInvalidate();
  return useMutation({
    mutationFn: ({
      body,
      eventId,
      allowConflict,
    }: {
      body: EventWrite | Partial<EventWrite>;
      eventId?: string;
      allowConflict?: boolean;
    }) => {
      const query = allowConflict ? "?allow_conflict=true" : "";
      return eventId
        ? request<OccupancyEvent>(`/events/${eventId}${query}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          })
        : request<OccupancyEvent>(`/events${query}`, {
            method: "POST",
            body: JSON.stringify(body),
          });
    },
    onSuccess: invalidate,
  });
}

export function useDeleteEvent() {
  const request = useOccupancyRequest();
  const invalidate = useOccupancyInvalidate();
  return useMutation({
    mutationFn: (eventId: string) => request<void>(`/events/${eventId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useRestoreEvent() {
  const request = useOccupancyRequest();
  const invalidate = useOccupancyInvalidate();
  return useMutation({
    mutationFn: (eventId: string) =>
      request<OccupancyEvent>(`/events/${eventId}/restore`, { method: "POST" }),
    onSuccess: invalidate,
  });
}

export function useSaveBasePattern() {
  const request = useOccupancyRequest();
  const invalidate = useOccupancyInvalidate();
  return useMutation({
    mutationFn: (body: { site_id: string; effective_from: string; days: BasePatternDay[] }) =>
      request<BasePattern>("/base-pattern", { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

/**
 * Upsert one day's base covers.
 *
 * `source` is explicit rather than fixed to `page`, because it is what decision 3's
 * weekly-entered identity is read from (`weekly_entered ≡ source != 'page'`) and two callers
 * genuinely differ. A single day typed by hand is `page` wherever it was typed — the widget's
 * per-day edit included, since a daily figure is exactly what decision 9 must not re-split.
 * Restoring a removed day is the other case: it has to put back the source the day held, or
 * undo would silently convert a widget split into seven daily edits.
 */
export function useSaveOverride() {
  const request = useOccupancyRequest();
  const invalidate = useOccupancyInvalidate();
  return useMutation({
    mutationFn: ({
      source,
      ...body
    }: {
      site_id: string;
      date: string;
      covers: number;
      source: "page" | "widget";
    }) =>
      request<BaseOverride>(`/overrides?source=${source}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useRemoveOverride() {
  const request = useOccupancyRequest();
  const invalidate = useOccupancyInvalidate();
  return useMutation({
    mutationFn: ({ siteId, date }: { siteId: string; date: string }) =>
      request<void>(`/overrides?site_id=${siteId}&date=${date}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

/**
 * The Dashboard's covers figures, drawn from the occupancy store (POS2-1259 CP5).
 *
 * Replaces `use-covers.ts` and its `estimateRangeCovers` approximation entirely: the endpoint
 * returns per-day covers, so there is nothing left to prorate. Decision 9's granularity rule is
 * applied by `sumCoversForRange`, which the backend mirrors exactly.
 */
export function useOccupancyRangeCovers(
  siteId: string | null,
  range: { from: string | null; to: string | null },
) {
  const query = useOccupancyCovers(siteId, range.from ?? "", range.to ?? "");
  const summary =
    query.data && range.from && range.to
      ? sumCoversForRange(query.data, range.from, range.to, "partial-weeks")
      : null;
  return { ...query, summary };
}

/**
 * Save a weekly figure as seven daily overrides flagged `source='widget'` (POS2-1259 CP5).
 *
 * The widget shows weeks; the store holds days. One upsert per day rather than a bulk endpoint,
 * because the API's override write is already `(site, date)`-keyed and adding a second shape for
 * the same write would be a second way to get the source flag wrong.
 */
export function useSaveWeeklyCovers() {
  const request = useOccupancyRequest();
  const invalidate = useOccupancyInvalidate();
  return useMutation({
    mutationFn: async ({
      siteId,
      weekStart,
      covers,
      pattern,
    }: {
      siteId: string;
      weekStart: string;
      covers: number;
      pattern: BasePatternDay[] | null;
    }) => {
      const split = splitWeekByPattern(covers, weekStart, pattern);
      const dates = weekDates(weekStart);
      // Together, not one after another. Seven writes awaited in sequence is seven round trips
      // the user waits through to save one figure, and nothing orders them: the upsert key is
      // `(site, date)` and these are seven distinct dates, so they cannot contend.
      await Promise.all(
        dates.map((date, index) =>
          request<BaseOverride>("/overrides?source=widget", {
            method: "PUT",
            body: JSON.stringify({ site_id: siteId, date, covers: split[index] }),
          }),
        ),
      );
    },
    onSuccess: invalidate,
  });
}

/**
 * Every entered day for the site, newest first — not bounded by the visible month.
 *
 * The base covers drawer lists these, and a list limited to whatever month is on screen would
 * hide most of what a user has set. Same call `food-spend-dialog.tsx` makes for its weeks list.
 */
export function useOverrides(siteId: string | null, enabled = true) {
  const request = useOccupancyRequest();
  const headers = useApiHeaders();
  return useQuery<OverrideList>({
    queryKey: ["occupancy", "overrides", siteId],
    queryFn: () => request<OverrideList>(`/overrides?site_id=${siteId as string}`),
    enabled: enabled && !!siteId && !!headers.Authorization.replace("Bearer ", ""),
  });
}
